import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Download, Loader2, Plus, Shield, X } from 'lucide-react';
import AppBar from 'components/AppBar';
import { useAuthSession } from '../contexts/AuthSessionContext';
import { isPlatformSuperAdmin } from '../services/authService';
import { DataTable } from 'components/data-table/DataTable';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Skeleton } from 'components/ui/skeleton';
import {
  createTeamMember,
  getAllTeamMembers,
  updateTeamMember,
} from '../services/teamService';
import { createTeamColumns } from './team/columns';
import './Team.css';

const LOT_OPTIONS = ['LOT 8 (Morogoro)', 'LOT 10 (Manyara)', 'Both'];

const POSITION_OPTIONS = [
  'Project Manager',
  'Site Manager',
  'Site Supervisor',
  'HSE Officer',
  'Social Person',
  'Warehouse Manager',
  'Project Support Officer',
  'Fleet Manager',
  'Contract Manager',
];

const EMPTY_FORM = {
  name: '',
  lotName: 'LOT 8 (Morogoro)',
  position: '',
  contacts: '',
  email: '',
};

const TEAM_SKELETON_HEADERS = ['', 'Name', 'Lot', 'Position', 'Contacts', 'Email', ''];
const TEAM_SKELETON_ROWS = 8;

function TeamSkeleton() {
  return (
    <div className="team-skeleton" aria-busy="true" aria-label="Loading team members">
      <section className="team-intro" aria-hidden>
        <div className="team-intro-text">
          <Skeleton className="h-7 w-44" />
          <Skeleton className="mt-2.5 h-4 w-80 max-w-full" />
        </div>
        <div className="team-intro-actions">
          <Skeleton className="h-9 w-[8.5rem] rounded-lg" />
        </div>
      </section>

      <div className="team-table-section" aria-hidden>
        <div className="data-table flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-full max-w-sm rounded-lg" />
            <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
          </div>

          <div className="data-table__shell overflow-hidden rounded-lg border border-solid border-border">
            <table className="team-skel-table w-full">
              <thead>
                <tr>
                  {TEAM_SKELETON_HEADERS.map((label, i) => (
                    <th key={`team-skel-h-${i}`}>
                      {label ? (
                        <span className="team-skel-th-label">{label}</span>
                      ) : (
                        <Skeleton className="h-4 w-4 rounded" />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: TEAM_SKELETON_ROWS }, (_, row) => (
                  <tr key={`team-skel-r-${row}`}>
                    <td>
                      <Skeleton className="h-4 w-4 rounded" />
                    </td>
                    <td>
                      <Skeleton className="h-4 w-[8.5rem] max-w-full" />
                    </td>
                    <td>
                      <Skeleton className="h-4 w-28 max-w-full" />
                    </td>
                    <td>
                      <Skeleton className="h-4 w-32 max-w-full" />
                    </td>
                    <td>
                      <Skeleton className="h-4 w-28 max-w-full" />
                    </td>
                    <td>
                      <Skeleton className="h-4 w-40 max-w-full" />
                    </td>
                    <td>
                      <Skeleton className="ml-auto h-8 w-8 rounded-md" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="team-skel-pagination flex items-center justify-between px-2">
            <Skeleton className="hidden h-4 w-40 lg:block" />
            <div className="flex w-full items-center justify-end gap-4 lg:w-fit">
              <Skeleton className="hidden h-4 w-28 lg:block" />
              <Skeleton className="h-4 w-24" />
              <div className="flex items-center gap-2">
                <Skeleton className="hidden size-8 rounded-md lg:block" />
                <Skeleton className="size-8 rounded-md" />
                <Skeleton className="size-8 rounded-md" />
                <Skeleton className="hidden size-8 rounded-md lg:block" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Team() {
  const session = useAuthSession();
  const showUserAccess = isPlatformSuperAdmin(session);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const members = await getAllTeamMembers();
        if (!cancelled) setTeamMembers(members);
      } catch (err) {
        console.error('Error loading team members:', err);
        if (!cancelled) setError('Failed to load team members. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleOpenAddModal = () => {
    setIsEditing(false);
    setEditingMember(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  const handleOpenEditModal = (member) => {
    setIsEditing(true);
    setEditingMember(member);
    setFormData({
      name: member.name,
      lotName: member.lotName,
      position: member.position,
      contacts: member.contacts,
      email: member.email,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setEditingMember(null);
    setFormData(EMPTY_FORM);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (isEditing && editingMember) {
        await updateTeamMember(editingMember.id, formData);
      } else {
        await createTeamMember(formData);
      }
      const members = await getAllTeamMembers();
      setTeamMembers(members);
      handleCloseModal();
    } catch (err) {
      console.error('Error saving team member:', err);
      setError('Failed to save team member. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleExportToExcel = (rows) => {
    const exportData = rows.map((member) => ({
      Name: member.name,
      'Lot Name': member.lotName,
      Position: member.position,
      Contacts: member.contacts,
      'Email Address': member.email,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Team Members');
    ws['!cols'] = [
      { wch: 25 },
      { wch: 20 },
      { wch: 20 },
      { wch: 18 },
      { wch: 30 },
    ];
    XLSX.writeFile(wb, `Team_Members_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const columns = useMemo(
    () => createTeamColumns({ onEdit: handleOpenEditModal }),
    []
  );

  return (
    <div className="team-page">
      <div className="team-scroll">
        <div className="team-panel">
          <AppBar
            variant="inset"
            breadcrumbs={[
              { label: 'Platform', hideOnMobile: true },
              { label: 'Team' },
            ]}
          />

          {loading ? (
            <TeamSkeleton />
          ) : (
            <>
              <section className="team-intro" aria-label="Team overview">
                <div className="team-intro-text">
                  <h1 className="team-intro-title">Team members</h1>
                  <p className="team-intro-subtitle">
                    Manage people across LOT 8, LOT 10, and shared roles
                  </p>
                </div>
                <div className="team-intro-actions">
                  {showUserAccess ? (
                    <Button type="button" variant="outline" className="team-action-btn" asChild>
                      <Link to="/team/user-access">
                        <Shield className="h-4 w-4" />
                        User access
                      </Link>
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    className="team-action-btn"
                    onClick={handleOpenAddModal}
                  >
                    <Plus className="h-4 w-4" />
                    Add member
                  </Button>
                </div>
              </section>

              {error ? (
                <div className="team-error" role="alert">
                  {error}
                </div>
              ) : null}

              <div className="team-table-section">
                <DataTable
                  columns={columns}
                  data={teamMembers}
                  searchPlaceholder="Search name, lot, position, contact, or email…"
                  toolbarActions={(table) => (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="team-export-btn team-soft-btn h-9 w-9"
                      onClick={() =>
                        handleExportToExcel(
                          table.getFilteredRowModel().rows.map((row) => row.original)
                        )
                      }
                      disabled={table.getFilteredRowModel().rows.length === 0}
                      title="Download Excel"
                      aria-label="Download Excel"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  )}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {showModal ? (
        <div className="team-modal-overlay" onClick={handleCloseModal}>
          <div
            className="team-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="team-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="team-modal__header">
              <h2 id="team-modal-title">{isEditing ? 'Edit team member' : 'Add team member'}</h2>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="team-modal__close team-soft-btn"
                onClick={handleCloseModal}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="team-modal__form">
              <div className="team-field">
                <Label htmlFor="team-name">Name</Label>
                <Input
                  id="team-name"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  required
                  placeholder="Full name"
                />
              </div>
              <div className="team-field">
                <Label htmlFor="team-lot">Lot</Label>
                <select
                  id="team-lot"
                  className="team-select"
                  value={formData.lotName}
                  onChange={(e) => handleFormChange('lotName', e.target.value)}
                  required
                >
                  {LOT_OPTIONS.map((lot) => (
                    <option key={lot} value={lot}>
                      {lot}
                    </option>
                  ))}
                </select>
              </div>
              <div className="team-field">
                <Label htmlFor="team-position">Position</Label>
                <select
                  id="team-position"
                  className="team-select"
                  value={formData.position}
                  onChange={(e) => handleFormChange('position', e.target.value)}
                  required
                >
                  <option value="">Select position</option>
                  {POSITION_OPTIONS.map((position) => (
                    <option key={position} value={position}>
                      {position}
                    </option>
                  ))}
                </select>
              </div>
              <div className="team-field">
                <Label htmlFor="team-contacts">Contacts</Label>
                <Input
                  id="team-contacts"
                  type="tel"
                  value={formData.contacts}
                  onChange={(e) => handleFormChange('contacts', e.target.value)}
                  required
                  placeholder="+255 XXX XXX XXX"
                />
              </div>
              <div className="team-field">
                <Label htmlFor="team-email">Email</Label>
                <Input
                  id="team-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  required
                  placeholder="email@example.com"
                />
              </div>
              <div className="team-modal__actions">
                <Button
                  type="button"
                  variant="ghost"
                  className="team-soft-btn"
                  onClick={handleCloseModal}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : isEditing ? (
                    'Update member'
                  ) : (
                    'Add member'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Team;
